import { useEffect, useRef, useState } from "react";

export type StoreCategoryScope = { params: string; releaseId: string };
type CategoryIdentity = { label: string; store?: string };
type Result<T> = { key: string; rows?: T[]; error?: string };

export type StoreCategoryExportView<T = CategoryIdentity> = { rows: T[]; byStore: boolean; loading: boolean; error?: string; search: string; registryKey: string };
const exportViews = new Map<string, StoreCategoryExportView>();
const scopeKey = (scope: StoreCategoryScope) => JSON.stringify([scope.releaseId, scope.params]);
export function registerStoreCategoryExport<T extends CategoryIdentity>(scope: StoreCategoryScope, view: StoreCategoryExportView<T>) {
  const key = scopeKey(scope);
  exportViews.set(key, view);
  return () => { if (exportViews.get(key) === view) exportViews.delete(key); };
}
export function readStoreCategoryExport<T extends CategoryIdentity>(scope: StoreCategoryScope): StoreCategoryExportView<T> | undefined {
  const view = exportViews.get(scopeKey(scope));
  if (view?.loading) throw Error("Mağaza ve kategori verilerinin yüklenmesini bekleyin.");
  if (view?.error) throw Error(view.error);
  return view as StoreCategoryExportView<T> | undefined;
}

export function storeCategoryRowId(row: CategoryIdentity) {
  return row.store == null ? row.label : JSON.stringify([row.store, row.label]);
}

const copy = {
  tr: { toggle: "Mağaza Bazlı", loading: "Mağaza ve kategori verileri yükleniyor…", error: "Mağaza ve kategori verileri açılamadı. Seçeneği kapatıp tekrar açabilirsiniz.", changed: "Veri sürümü değişti. Sayfayı yenileyin.", scope: "Ciro payı seçili dönemin toplamına; stok payı seçili mağazaların güncel toplam stokuna göredir." },
  en: { toggle: "By Store", loading: "Loading store and category data…", error: "Store and category data could not be loaded. Turn the option off and on to retry.", changed: "The data release changed. Reload the page.", scope: "Revenue share uses the selected period total; stock share uses total current stock in the selected stores." },
  ru: { toggle: "По магазинам", loading: "Загрузка данных магазинов и категорий…", error: "Не удалось загрузить данные. Выключите и включите опцию для повторной попытки.", changed: "Версия данных изменилась. Обновите страницу.", scope: "Доля выручки — от итога выбранного периода; доля запаса — от текущего общего запаса выбранных магазинов." },
  uz: { toggle: "Do‘konlar bo‘yicha", loading: "Do‘kon va toifa ma’lumotlari yuklanmoqda…", error: "Ma’lumotlarni yuklab bo‘lmadi. Qayta urinish uchun parametrni o‘chirib yoqing.", changed: "Ma’lumotlar versiyasi o‘zgardi. Sahifani yangilang.", scope: "Tushum ulushi tanlangan davr jami bo‘yicha; zaxira ulushi tanlangan do‘konlarning joriy jami zaxirasi bo‘yicha." },
};
export function storeCategoryCopy(language: string) { return copy[language as keyof typeof copy] ?? copy.tr; }

// Lazy, release-scoped GET only. A prior filter or period is never shown as
// the new scope; switching back to the original table aborts the optional read.
export function useStoreCategoryRows<T extends CategoryIdentity>(original: T[], scope: StoreCategoryScope | undefined, enabled: boolean, language: string) {
  const key = enabled && scope ? JSON.stringify([scope.releaseId, scope.params]) : "";
  const [result, setResult] = useState<Result<T>>({ key: "" });
  const cache = useRef(new Map<string, T[]>());
  const text = storeCategoryCopy(language);
  useEffect(() => {
    if (!key || !scope) return;
    const cached = cache.current.get(key);
    if (cached) { setResult({ key, rows: cached }); return; }
    const controller = new AbortController();
    let active = true;
    setResult({ key });
    void (async () => {
      try {
        const response = await fetch(`/api/v1/store/categories?${scope.params}`, { signal: controller.signal });
        if (!response.ok) throw Error(text.error);
        const data = await response.json();
        const requested = new URLSearchParams(scope.params);
        if (data.release_id !== scope.releaseId || data.period?.start !== requested.get("start") || data.period?.end !== requested.get("end")) throw Error(text.changed);
        if (!Array.isArray(data.rows) || data.rows.some((row: CategoryIdentity) => typeof row.store !== "string" || typeof row.label !== "string")) throw Error(text.error);
        if (!active) return;
        if (cache.current.size >= 8) cache.current.delete(cache.current.keys().next().value!);
        cache.current.set(key, data.rows);
        setResult({ key, rows: data.rows });
      } catch (error) {
        if (active) setResult({ key, error: error instanceof Error ? error.message : text.error });
      }
    })();
    return () => { active = false; controller.abort(); };
  }, [key, text]);
  const current = key && result.key === key ? result : undefined;
  return { rows: !key ? original : current?.rows ?? [], loading: !!key && !current?.rows && !current?.error, error: current?.error, copy: text };
}
